<template>
  <div>
    <form style="visibility:hidden; height: 1px;" ref="schemaFileForm">
      <input
        accept="application/json"
        type="file"
        ref="importSchema"
        @change="filePick($event)"
      />
    </form>
    <popup2 :value="!!event" @input="event = null" width="428">
      <edit-field
        v-if="event === 'addGroup'"
        :type="'chips'"
        :title="'Add schema to groups'"
        :field="'Groups'"
        :value="[]"
      />
      <default-confirmation
        text-delete="Delete this schemas completely?"
        v-else-if="event === 'delete'"
        @close="event = null"
        @delete="deleteAction"
      />
      <component
        v-else-if="event"
        :entity="{}"
        :is="event"
        @save="saveHandler"
        @close="event = null"
      />
    </popup2>
    <table-page
      :selected-out="eValue"
      :toolbar="toolbar"
      :table="table"
      :entity="true"
      @event="eventHandler"
    />
  </div>
</template>

<script>
import DefaultConfirmation from '@/components/app-entity/modals/DefaultConfirmation';
import pageActions from './pageActions';
import selectedActions from './selectedActions';
import {
  ref,
  computed,
  onMounted,
  watch,
  reactive,
  toRefs,
  onUnmounted
} from '@vue/composition-api';
import toSnakeCase from 'lodash.snakecase';
import useSchemas from '@/api/schemas';
import useSettings from '@/api/tableSettings';
import useGroups from '@/api/groups';
import { nanoid } from 'nanoid';

import useMedia from '@/compositions/useMedia';
import useAuth from '@/api/auth';
import { RELOAD_INTERVAL_SEC } from "@/utils/constants";
import cloneDeep from "lodash.clonedeep";
export default {
  name: 'schemas',
  metaInfo: {
    titleTemplate: '%s | Schemas'
  },
  components: {
    DefaultConfirmation,
    addNew: () => import('./edit')
  },
  setup(props, { root, refs }) {
    const { load: loadSettings, save: saveSettings } = useSettings();
    const { schemas: schemasApi, schema: schemaApi } = new useSchemas();
    const { groups: pureGroupsApi } = new useGroups();
    const { computeIcon } = useMedia();

    const { profile } = useAuth();
    const visibleColumns = ref([]);
    const allColumns = ref([
      'name',
      'mAuthor',
      'mManufacturer',
      'mVersion',
      'mEmail',
      'enabled',
      'type',
      'parentSchemaName',
      'editorgroup',
      'usergroup',
      'readergroup'
    ]);

    let timerId = null;
    const { total, list, load: loadList, params, setParams } = schemasApi;

    watch(
      () => root.$route.query.filterTable,
      value => {
        if (value) {
          setParams({
            filter: {
              name: {
                includesInsensitive: value
              }
            }
          });
          load();
        } else {
          setParams({ filter: null });
          load();
        }
      }
    );
    function load(filterOuter = {}) {
      let filter = {
        ...params.value.filter,
        ...filterOuter
      };

      if (root.$route.query.filterTable !== undefined) {
        filter.name = {
          includesInsensitive: root.$route.query.filterTable
        };
      }

      const setts = loadSettings('schemasSettings');

      if (setts) {
        visibleColumns.value = setts;
      }

      return loadList();
    }

    const event = ref(null);
    const eValue = ref(null);
    const toolbar = ref({
      title: 'schemas',
      pageActions,
      selectedActions
    });

    const settings = computed(() => {
      return visibleColumns.value;
    });

    const columns = computed(() => {
      return allColumns.value.map(item => ({
        key: item,
        text: root.$t(`Schema.${item}`),
        value: item
      }));
    });

    const items = computed(() =>
      list.value.map(i => ({
        ...i,
        type: i?.type ? i.type.toLowerCase() : '',
        iconFallback: 'mdi-card-bulleted',
        avatar: computeIcon(i)
      }))
    );
    const count = computed(() => total.value);
    const state = reactive({
      table: {
        filters: [],
        total: count,
        items,
        columns,
        settings,
        loading: false,
        itemRoute: 'schemasTableComponent'
      }
    });
    function deleteAction() {
      schemasApi.remove(eValue.value.map(i => ({ id: i.id }))).then(() => {
        load();
        event.value = null;
        eValue.value = [];
      });
    }

    function bumpVersionAction() {
      schemaApi.bumpVersion(eValue.value.map(i => i.id)).then(() => {
        load();
        event.value = null;
        eValue.value = [];
      });
    }

    function toggleSchemaEnabled(status) {
      schemaApi
        .updateSchemas(
          eValue.value.map(i => ({
            id: i.id,
            enabled: status
          }))
        )
        .then(() => {
          load();
          event.value = null;
          eValue.value = [];
        });
    }

    async function filePick(e) {
      const file = e.target.files[0];
      const url = URL.createObjectURL(file);
      let dataSchema = null;
      await fetch(url)
        .then(response => {
          return response.json();
        })
        .then(data => {
          dataSchema = data;
          return schemaApi.importJson(JSON.stringify(data));
        })
        .then(res => {
          let uuid = res.data.uuid || dataSchema.schema.id;
          if (uuid) {
            root.$router
              .push({
                name: 'schemasTableComponent',
                params: {
                  id: uuid
                },
                query: { timestamp: Date.now() }
              })
              .catch(e => ({}));
            load();
          }
        })
        .catch(() => {})
        .finally(() => {
          URL.revokeObjectURL(url);
          root.$router.replace({ query: null });
          refs.schemaFileForm.reset();
        });
    }
    function eventHandler({ event: e, data }) {
      eValue.value = data;
      switch (e) {
        case 'setSettings': {
          visibleColumns.value = data;
          saveSettings('schemasSettings', data);
          break;
        }
        case 'addNew': {
          event.value = 'addNew';
          break;
        }
        case 'importSchema': {
          refs.importSchema.click();
          break;
        }
        case 'delete': {
          event.value = 'delete';
          break;
        }
        case 'bump-version': {
          bumpVersionAction();
          break;
        }
        case 'massiveDisableSchemas': {
          toggleSchemaEnabled(false);
          break;
        }
        case 'massiveEnableSchemas': {
          toggleSchemaEnabled(true);
          break;
        }
        case 'addGroup': {
          event.value = 'addGroup';
          break;
        }
        case 'setFilters': {
          state.table.filters = cloneDeep(data);
          setParams({ offset: 0 });
          let filter = {};
          data
            .filter(i => {
              if (Array.isArray(i.model) && i.model.length > 0) {
                return true;
              } else if (!Array.isArray(i.model) && i.model.value !== null) {
                return true;
              }
              return false;
            })
            .forEach(i => {
              filter = {
                ...filter,
                ...i?.getFilter()
              };
            });
          setParams({ filter, offset: 0 });
          load(filter);
          break;
        }
        case 'filteringTable': {
          const result = {};

          if (data.itemsPerPage) {
            result.first = data.itemsPerPage;
          }

          result.offset = (data.page - 1) * data.itemsPerPage;

          if (data.desc) {
            setParams({ desc: data.desc.length ? data.desc[0] : false });
          }

          if (data.sort) {
            if (data.sort.length) {
              setParams({ sort: data.sort[0] });
            } else {
              setParams({ sort: null });
              setParams({ orderBy: 'NATURAL' });
            }
          }

          if (data.desc && params.value.sort) {
            result.orderBy = toSnakeCase(params.value.sort).toUpperCase();
            result.orderBy += `_${params.value.desc ? 'ASC' : 'DESC'}`;
          }

          setParams(result);
          load();
          break;
        }
      }
    }
    function saveHandler(r) {
      switch (event.value) {
        case 'addNew':
          event.value = null;
          root.$router.push({
            name: 'schemasTableComponent',
            params: { id: r.data.createSchema.schema.id }
          });
          load();
          break;
      }
    }

    onUnmounted(() => {
      clearInterval(timerId);
    });

    onMounted(async () => {
      setParams({
        filter: {},
        first:
          profile.value?.objectProperties.find(
            item => item.key === 'programNumbersOfRows'
          )?.value || 20
      });
      timerId = setInterval(load, RELOAD_INTERVAL_SEC);

      const setts = loadSettings('schemasSettings');

      if (setts) {
        visibleColumns.value = setts;
      } else {
        visibleColumns.value = allColumns.value;
      }

      await load();
      await pureGroupsApi.load().then(data => {
        state.usersGroup = data.userGroups.map(group => ({
          id: group.id,
          value: group.id,
          name: group.groupName
        }));
      });
      await root.$nextTick();

      const filters = [
        {
          id: 'enabled',
          title: 'Status',
          type: 'tags',
          getFilter() {
            return {
              enabled: {
                in: this.model.map(item => item.value)
              }
            };
          },
          model: [],
          values: [
            { name: 'Enabled', id: nanoid(), value: true },
            { name: 'Disabled', id: nanoid(), value: false }
          ]
        },
        {
          id: 'tags',
          title: 'Tags',
          model: [],
          getFilter() {
            return {
              mTags: {
                contains: this.model
              }
            };
          },
          type: 'tags-live',
          values: []
        },
        {
          id: 'manufacturer',
          title: 'Manufacturer',
          model: [],
          getFilter() {
            return {
              mManufacturer: {
                inInsensitive: this.model
              }
            };
          },
          type: 'tags-live',
          values: []
        },
        {
          id: 'schemaType',
          title: 'Schema type',
          getFilter() {
            return {
              type: {
                in: this.model.map(item => item.value)
              }
            };
          },
          type: 'tags',
          model: [],
          values: [
            { name: 'application', id: nanoid(), value: 'APPLICATION' },
            { name: 'dataset', id: nanoid(), value: 'DATASET' },
            { name: 'device', id: nanoid(), value: 'DEVICE' },
            { name: 'media', id: nanoid(), value: 'MEDIA' }
          ]
        },
        {
          getFilter() {
            return {
              editorgroup: {
                in: this.model.map(item => item.id)
              }
            };
          },
          id: 'Editors group',
          model: [],
          type: 'tags',
          values: state.usersGroup,
          title: 'Editors group'
        },
        {
          getFilter() {
            return {
              usergroup: {
                in: this.model.map(item => item.id)
              }
            };
          },
          id: 'Users group',
          model: [],
          type: 'tags',
          values: state.usersGroup,
          title: 'Users group'
        },
        {
          getFilter() {
            return {
              readergroup: {
                in: this.model.map(item => item.id)
              }
            };
          },
          id: 'Readers group',
          model: [],
          type: 'tags',
          values: state.usersGroup,
          title: 'Readers group'
        }
      ];
      state.table.filters = cloneDeep(filters);
      state.table.emptyFilters = cloneDeep(filters);
    });
    return {
      ...toRefs(state),
      event,
      toolbar,
      eventHandler,
      saveHandler,
      filePick,
      deleteAction,
      bumpVersionAction,
      eValue
    };
  }
};
</script>
